import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  MapPin,
  Router as RouterIcon,
  Users,
  Ticket,
  Receipt,
  BarChart3,
  MessageSquare,
  ShieldCheck,
  LogOut,
  Wifi,
  Zap,
  Sun,
  Moon,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Cable,
  Network,
  Map,
  FileBarChart,
  Globe,
  Settings as SettingsIcon,
  Radio,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useAdminTheme } from "@/context/AdminThemeContext";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";

const SIDEBAR_COLLAPSED_KEY = "nhp_admin_sidebar_collapsed";

/** Wraps a collapsed-rail icon with its label as a tooltip; a no-op pass-through otherwise. */
function Tip({ collapsed, label, children }) {
  if (!collapsed || !label) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right" className="bg-nhp-text text-nhp-bg border-none font-mono text-[11px] uppercase tracking-wider">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

const nav = [
  { to: "/admin/dashboard", label: "Overview", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/admin/packages", label: "Packages", icon: Package, id: "nav-packages" },
  { to: "/admin/sites", label: "Sites", icon: MapPin, id: "nav-sites" },
  { to: "/admin/routers", label: "Routers", icon: RouterIcon, id: "nav-routers" },
  { to: "/admin/provision", label: "Provision Site", icon: Zap, id: "nav-provision" },
  { to: "/admin/customers", label: "Customers", icon: Users, id: "nav-customers" },
  { to: "/admin/subscriptions", label: "Subscriptions", icon: Ticket, id: "nav-subscriptions" },
  { to: "/admin/live-devices", label: "Live Devices", icon: Radio, id: "nav-live-devices" },
  { to: "/admin/pppoe-plans", label: "PPPoE Plans", icon: Cable, id: "nav-pppoe-plans" },
  { to: "/admin/pppoe-locations", label: "Locations", icon: Globe, id: "nav-pppoe-locations" },
  { to: "/admin/pppoe-accounts", label: "PPPoE Accounts", icon: Network, id: "nav-pppoe-accounts" },
  { to: "/admin/pppoe-accounts/map", label: "PPPoE Map", icon: Map, id: "nav-pppoe-map" },
  { to: "/admin/pppoe-accounts/reports", label: "PPPoE Reports", icon: FileBarChart, id: "nav-pppoe-reports" },
  { to: "/admin/transactions", label: "Transactions", icon: Receipt, id: "nav-transactions" },
  { to: "/admin/vouchers", label: "Vouchers", icon: Ticket, id: "nav-vouchers" },
  { to: "/admin/reports", label: "Reports", icon: BarChart3, id: "nav-reports" },
  { to: "/admin/admins", label: "Admins", icon: ShieldCheck, id: "nav-admins" },
  { to: "/admin/sms", label: "SMS Outbox", icon: MessageSquare, id: "nav-sms" },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, id: "nav-settings" },
];

function SidebarContent({ user, theme, toggleTheme, handleLogout, onNavigate, collapsed = false, showCollapseToggle = false, onToggleCollapse }) {
  return (
    <>
      <div className={`border-b border-nhp-border ${collapsed ? "px-2 py-4 flex flex-col items-center gap-3" : "px-6 py-6 flex items-center justify-between"}`}>
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-9 w-9 grid place-items-center bg-nhp-primary text-nhp-on-primary shrink-0">
            <Wifi className="h-5 w-5" strokeWidth={2.5} />
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <div className="font-heading font-black tracking-tight text-lg leading-none truncate">
                NetOnLain
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted mt-1">
                Hotspot Platform
              </div>
            </div>
          )}
        </div>
        {showCollapseToggle && (
          <Tip collapsed={collapsed} label="Expand sidebar">
            <button
              type="button"
              data-testid="sidebar-collapse-toggle"
              onClick={onToggleCollapse}
              title={collapsed ? undefined : "Collapse sidebar"}
              className="h-8 w-8 shrink-0 grid place-items-center text-nhp-muted hover:text-nhp-text hover:bg-nhp-surface"
            >
              {collapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
            </button>
          </Tip>
        )}
      </div>

      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {nav.map((item) => {
          const Icon = item.icon;
          return (
            <Tip key={item.to} collapsed={collapsed} label={item.label}>
              <NavLink
                to={item.to}
                end={item.to === "/admin/pppoe-accounts"}
                data-testid={item.id}
                onClick={onNavigate}
                className={({ isActive }) =>
                  `flex items-center gap-3 py-2.5 text-sm font-medium ${collapsed ? "justify-center px-0" : "px-3"} ${
                    isActive
                      ? "bg-nhp-surface text-nhp-primary border-l-2 border-nhp-primary"
                      : "text-nhp-muted hover:text-nhp-text hover:bg-nhp-surface border-l-2 border-transparent"
                  }`
                }
              >
                <Icon className="h-5 w-5 shrink-0" strokeWidth={1.75} />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            </Tip>
          );
        })}
      </nav>

      <div className={`border-t border-nhp-border shrink-0 ${collapsed ? "p-2" : "p-3"}`}>
        {!collapsed && (
          <div className="px-3 py-2 mb-2">
            <div className="text-xs text-nhp-muted font-mono">SIGNED IN</div>
            <div className="text-sm truncate">{user?.email}</div>
          </div>
        )}
        <Tip collapsed={collapsed} label={theme === "dark" ? "Light mode" : "Dark mode"}>
          <button
            data-testid="theme-toggle-btn"
            onClick={toggleTheme}
            className={`w-full flex items-center gap-2 py-2 text-sm text-nhp-muted hover:text-nhp-text hover:bg-nhp-surface ${collapsed ? "justify-center px-0" : "px-3"}`}
          >
            {theme === "dark" ? <Sun className="h-5 w-5" strokeWidth={1.75} /> : <Moon className="h-5 w-5" strokeWidth={1.75} />}
            {!collapsed && (theme === "dark" ? "Light mode" : "Dark mode")}
          </button>
        </Tip>
        <Tip collapsed={collapsed} label="Sign out">
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className={`w-full flex items-center gap-2 py-2 text-sm text-nhp-muted hover:text-nhp-critical hover:bg-nhp-surface ${collapsed ? "justify-center px-0" : "px-3"}`}
          >
            <LogOut className="h-5 w-5" strokeWidth={1.75} />
            {!collapsed && "Sign out"}
          </button>
        </Tip>
      </div>
    </>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useAdminTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1"; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, desktopCollapsed ? "1" : "0"); } catch { /* private mode etc — non-fatal */ }
  }, [desktopCollapsed]);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const currentLabel = nav.find((n) => location.pathname.startsWith(n.to))?.label || "NetOnLain";

  return (
    <div className={`nhp-${theme} min-h-screen flex bg-nhp-bg text-nhp-text`}>
      {/* Desktop sidebar */}
      <aside className={`hidden md:flex ${desktopCollapsed ? "md:w-[72px]" : "md:w-64"} shrink-0 border-r border-nhp-border bg-nhp-bg flex-col transition-[width] duration-200`}>
        <TooltipProvider delayDuration={0} skipDelayDuration={0}>
          <SidebarContent
            user={user} theme={theme} toggleTheme={toggleTheme} handleLogout={handleLogout}
            collapsed={desktopCollapsed} showCollapseToggle
            onToggleCollapse={() => setDesktopCollapsed((v) => !v)}
          />
        </TooltipProvider>
      </aside>

      {/* Mobile off-canvas drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-72 max-w-[85vw] h-full bg-nhp-bg border-r border-nhp-border flex flex-col">
            <button
              data-testid="mobile-sidebar-close"
              onClick={() => setMobileOpen(false)}
              className="absolute top-4 right-3 h-9 w-9 grid place-items-center text-nhp-muted hover:text-nhp-text"
            >
              <X className="h-5 w-5" />
            </button>
            <SidebarContent user={user} theme={theme} toggleTheme={toggleTheme} handleLogout={handleLogout} onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <div className="md:hidden sticky top-0 z-30 flex items-center gap-3 px-4 h-14 border-b border-nhp-border bg-nhp-bg shrink-0">
          <button
            data-testid="mobile-sidebar-open"
            onClick={() => setMobileOpen(true)}
            className="h-9 w-9 -ml-2 grid place-items-center text-nhp-text"
          >
            <Menu className="h-5 w-5" />
          </button>
          <span className="font-heading font-black text-sm truncate">{currentLabel}</span>
        </div>
        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
